import { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { cache, request, validateBusinessAccountName } from "../../../utils";
import Spinner from "../../ui/Spinner/Spinner";
import { ChevronDownIcon } from "lucide-react";
import { Modal } from "../../ui/modal";
import { PaymentDetailsModal } from "./PaymentDetailsModal";
import { PayLink, PaymentDetails } from "../types";
import { WalletSelectionModal } from "../../ui/modal/WalletSelectionModal";
import { PaymentLinkSelectMethod } from "./PaymentLinkSelectMethod";
import { useCurrencyStore } from "../../../stores/useCurrencyStore";
import { resolvePaymentMethod } from "../../../utils/helpers";

interface Currency {
  code: string;
  name: string;
  symbol: string;
  iso2: string;
  flag: string;
  hasBankList?: boolean;
  paymentMethod: {
    bankTransfer: boolean;
    mobileMoney: boolean;
    stableCurrency?: boolean;
  };
  supportedNetworks?: string[];
}

interface USER {
  businessName: string;
}

export const CreatePaymentLinkModal = ({
  isOpen,
  onClose,
  onCreate,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (createdLink: PayLink) => void;
}) => {
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(
    null,
  );
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [amount, setAmount] = useState("");
  const [displayMessage, setDisplayMessage] = useState("");
  const [paymentDetails, setPaymentDetails] = useState<any>(null);
  const [receiveTo, setReceiveTo] = useState("");
  const [payerName, setPayerName] = useState("");
  const [payerEmail, setPayerEmail] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<
    | "BANK_TRANSFER"
    | "STABLE_CURRENCY"
    | "MOBILE_MONEY"
    | "WECHAT_PAY"
    | "ALIPAY"
  >("BANK_TRANSFER");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [storedAccountName, setStoredAccountName] = useState("");
  const [accountNameError, setAccountNameError] = useState("");

  // ---- New states for wallet selection flow
  const [showMethodTypeModal, setShowMethodTypeModal] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  const [currencySearch, setCurrencySearch] = useState("");
  const { getFilteredCurrencies, loadingCurrency, hasLoaded } =
    useCurrencyStore();

  // ---- States for expiration
  const [expiresAtDate, setExpiresAtDate] = useState<Date | null>(null);

  const rawCurrencies = getFilteredCurrencies({});
  const paymentLinkCurrencies = rawCurrencies;


  // ---- Handle date change
  const handleDateChange = (date: Date | null) => {
    if (date) {
      const updatedDate = new Date(date);
      updatedDate.setHours(23, 59, 59, 999);
      setExpiresAtDate(updatedDate);
    } else {
      setExpiresAtDate(null);
    }
  };

  // ---- Reset all form fields when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setShowCurrencyDropdown(false);
      setAmount("");
      setDisplayMessage("");
      setPaymentDetails(null);
      setReceiveTo("");
      setPayerName("");
      setPayerEmail("");
      setPaymentMethod("BANK_TRANSFER");
      setError(null);
      setShowMethodTypeModal(false);
      setShowWalletModal(false);
      setShowPaymentDetailsModal(false);
      setStoredAccountName("");
      setAccountNameError("");
      setExpiresAtDate(null);
      setCurrencySearch("");
    }
  }, [isOpen]);

  // ---- Set default currency when currencies are loaded
  useEffect(() => {
    if (
      isOpen &&
      hasLoaded &&
      paymentLinkCurrencies.length > 0 &&
      !selectedCurrency
    ) {
      const ngnCurrency = paymentLinkCurrencies.find((c) => c.code === "NGN");
      setSelectedCurrency(ngnCurrency || paymentLinkCurrencies[0]);
    }
  }, [isOpen, hasLoaded, paymentLinkCurrencies, selectedCurrency]);

  // ---- Fetch User from cache for validation
  useEffect(() => {
    if (isOpen) {
      const fetchUser = () => {
        const cachedUser = cache.get("USER") as USER;
        if (cachedUser) {
          const checkedName = cachedUser.businessName;
          setStoredAccountName(checkedName);
        }
      };
      fetchUser();
    }
  }, [isOpen]);

  // ---- Handle amount change
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let rawValue = e.target.value.replace(/,/g, ""); // strip commas first

    // Allow only numbers and decimals
    if (/^\d*\.?\d*$/.test(rawValue) || rawValue === "") {
      setAmount(rawValue);
      setError("");
    }
  };

  // ---- Handle currency selection
  const handleSelectCurrency = (currency: Currency) => {
    setSelectedCurrency(currency);
    setShowCurrencyDropdown(false);
    setCurrencySearch("");

    if (selectedCurrency?.code !== currency.code) {
      setPaymentDetails(null);
      setReceiveTo("");
    }

    if (currency.paymentMethod.stableCurrency) {
      setPaymentMethod("STABLE_CURRENCY" as any); // Update type later
    } else {
      const method = resolvePaymentMethod(
        currency.iso2,
        currency.paymentMethod,
      );
      setPaymentMethod(method);
    }

    const method = resolvePaymentMethod(currency.iso2, currency.paymentMethod);

    setPaymentMethod(method);
  };

  // ---- Handle currency dropdown click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowCurrencyDropdown(false);
        setCurrencySearch("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // ---- New handlers for selecting Transfer or wallet selection flow
  const handleReceiveToClick = () => {
    if (!selectedCurrency) return;
    setShowMethodTypeModal(true);
  };
  const handleMethodTypeSelect = (
    methodType:
      | "WALLET"
      | "TRANSFER"
      | "STABLE_CURRENCY"
      | "WECHAT_PAY"
      | "ALIPAY",
  ) => {
    setShowMethodTypeModal(false);

    if (methodType === "TRANSFER") {
      setShowPaymentDetailsModal(true);
    } else if (methodType === "WALLET") {
      setShowWalletModal(true);
    } else if (methodType === "STABLE_CURRENCY") {
      setPaymentMethod("STABLE_CURRENCY");
      setShowPaymentDetailsModal(true);
    } else if (methodType === "WECHAT_PAY") {
      setPaymentMethod("WECHAT_PAY" as any);
      setShowPaymentDetailsModal(true);
    } else if (methodType === "ALIPAY") {
      setPaymentMethod("ALIPAY" as any);
      setShowPaymentDetailsModal(true);
    }
  };

  // ---- Handle wallet selection
  const handleWalletSelect = (wallet: any) => {
    setShowWalletModal(false);

    const walletDetails = {
      accountName: wallet.walletName,
      accountNumber: wallet.id,
      providerName: "Wallet",
      paymentMethod: "WALLET",
      iso2: selectedCurrency?.iso2 || "",
      currency: selectedCurrency?.code || "",
      bankCode: "",
      saveDetails: false,
      walletBalance: wallet.balance,
    };

    setPaymentDetails(walletDetails);
    setReceiveTo(`Wallet (${walletDetails.accountName})`);
  };

  const handleSavePaymentDetails = (details: PaymentDetails) => {
    if (!selectedCurrency) return;

    const method = details.paymentMethod || paymentMethod;

    let builtDetails: any = {
      currency: selectedCurrency.code,
      paymentMethod: method,
      iso2: selectedCurrency.iso2,
    };

    if (method === "STABLE_CURRENCY") {
      builtDetails = {
        ...builtDetails,
        walletAddress: details.walletAddress,
        network: details.network,
      };
    } else if (method === "MOBILE_MONEY") {
      builtDetails = {
        ...builtDetails,
        accountName: details.accountName,
        accountNumber: details.accountNumber,
        network: details.network ?? details.providerName,
        providerName: details.providerName,
      };
    } else if (method === "WECHAT_PAY" || method === "ALIPAY") {
      builtDetails = {
        ...builtDetails,
        phoneNumber: details.phoneNumber,
        accountName: details.accountName,
      };
    } else if (method === "BANK_TRANSFER") {
      builtDetails = {
        ...builtDetails,
        accountName: details.accountName,
        accountNumber: details.accountNumber,
        bankCode: details.bankCode,
        providerName: details.providerName,
        ...((selectedCurrency.iso2 === "GB" || selectedCurrency.code === "EUR") &&
  details.holderCity && details.holderStreet && details.holderPostalCode && {
  holderCity: details.holderCity,
  holderStreet: details.holderStreet,
  holderPostalCode: details.holderPostalCode,
}),
      };
    }

    // Validate business name (skip for stable currency)
    if (method !== "STABLE_CURRENCY") {
      if (
        !validateBusinessAccountName(
          details.accountName || "",
          storedAccountName,
        )
      ) {
        setAccountNameError("Account name must match your business name.");
      } else {
        setAccountNameError("");
      }
    } else {
      setAccountNameError("");
    }

    setPaymentDetails(builtDetails);
    setPaymentMethod(builtDetails.paymentMethod);

    // Set display text
    if (method === "STABLE_CURRENCY") {
      setReceiveTo(
        `${details.walletAddress?.slice(0, 10)}...${details.walletAddress?.slice(-10)} (${details.network})`,
      );
    } else {
      setReceiveTo(details.accountName!);
    }

    setShowPaymentDetailsModal(false);
  };

  const validateEmail = (payerEmail: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(payerEmail);
  };

  // ---- Handle create payment link
  const handleCreatePaymentLink = async () => {
    if (
      !selectedCurrency ||
      !amount ||
      !payerName ||
      !payerEmail ||
      !paymentDetails ||
      !displayMessage
    ) {
      setError("Please fill in all required fields");
      return;
    }

    if (!validateEmail(payerEmail)) {
      setError("Please enter a valid email address");
      setTimeout(() => setError(""), 3000);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const expiresAt = expiresAtDate
        ? new Date(
            new Date(expiresAtDate).getFullYear(),
            new Date(expiresAtDate).getMonth(),
            new Date(expiresAtDate).getDate(),
            23,
            59,
            59,
          )
        : undefined;

      const payload = {
        amount: amount,
        currencyIso2: selectedCurrency.iso2,
        displayMessage: displayMessage,
        payerDetails: {
          name: payerName,
          email: payerEmail,
        },
        paymentDetails: {
          ...paymentDetails,
          currency: selectedCurrency.code,
          paymentMethod: paymentMethod,
          ...(paymentMethod === "STABLE_CURRENCY" && {
            network: paymentDetails.network,
            walletAddress: paymentDetails.walletAddress,
          }),
        },
        ...(expiresAt && { expiresAt }),
      };

      const response = await request.post<PayLink>({
        url: "/payment-link",
        data: payload,
      });

      if (response?.data) {
        onCreate(response.data);
      }
    } catch (error: any) {
      console.error("Failed to create payment link:", error);
      setError(error?.message || "Failed to create payment link");
    } finally {
      setLoading(false);
    }
  };

  // ---- Get currency symbol
  const currencySymbol = () => selectedCurrency?.symbol || "";

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-md">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6 sticky top-0 bg-white dark:bg-gray-900 z-10">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
            Create Payment Link
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            &times;
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 border border-red-200 rounded-lg dark:bg-red-900/20 dark:text-red-400 dark:border-red-900/30">
            {error}
          </div>
        )}

        {loadingCurrency && !hasLoaded ? (
          <div className="rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800 h-[300px] flex items-center justify-center">
            <Spinner className="h-10 w-10 text-blue-500" />
          </div>
        ) : (
          <div
            className="space-y-6"
            style={{ maxHeight: "calc(90vh - 100px)", overflowY: "auto" }}
          >
            {/* Currency and Amount */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative" ref={dropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                  Currency you want to receive
                </label>
                <div className="relative">
                  <button
                    type="button"
                    className="w-full px-4 py-2.5 pr-10 text-gray-700 bg-white border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white flex items-center justify-between"
                    onClick={() => {
                      setShowCurrencyDropdown(!showCurrencyDropdown);
                      if (!showCurrencyDropdown) setCurrencySearch("");
                    }}
                  >
                    {selectedCurrency ? (
                      <div className="flex items-center">
                        <img
                          src={selectedCurrency.flag}
                          alt={selectedCurrency.code}
                          className="w-5 h-5 mr-2"
                        />
                        <span>{selectedCurrency.name}</span>
                      </div>
                    ) : (
                      <span>Select currency</span>
                    )}
                    <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                  </button>
                  {showCurrencyDropdown && (
                    <div className="absolute z-[100] mt-1 w-full bg-white shadow-lg rounded-md text-base ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 dark:border dark:border-gray-700">
                      <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                        <input
                          type="text"
                          autoFocus
                          placeholder="Search currency..."
                          value={currencySearch}
                          onChange={(e) => setCurrencySearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="max-h-52 overflow-auto py-1">
                        {(() => {
                          const search = currencySearch.trim().toLowerCase();
                          const filtered = paymentLinkCurrencies.filter(
                            (c) =>
                              !search ||
                              c.name.toLowerCase().includes(search) ||
                              c.code.toLowerCase().includes(search),
                          );
                          return filtered.length === 0 ? (
                            <p className="px-4 py-2 text-sm text-gray-400">
                              No results
                            </p>
                          ) : (
                            filtered.map((currency) => (
                              <div
                                key={`${currency.code}-${currency.iso2}`}
                                className={`flex items-center px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer ${
                                  selectedCurrency?.code === currency.code
                                    ? "bg-gray-100 dark:bg-gray-700"
                                    : ""
                                }`}
                                onClick={() => {
                                  handleSelectCurrency(currency);
                                  setCurrencySearch("");
                                }}
                              >
                                <img
                                  src={currency.flag}
                                  alt={currency.code}
                                  className="w-5 h-5 mr-2"
                                />
                                <span className="text-gray-700 dark:text-white">
                                  {currency.name}
                                </span>
                                <span className="ml-auto text-xs text-gray-400">
                                  {currency.code}
                                </span>
                              </div>
                            ))
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                  Amount
                </label>
                <div className="relative">
                  <div
                    className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-2xl font-semibold min-w-[50px] whitespace-nowrap`}
                  >
                    {currencySymbol()}
                  </div>
                  <input
                    type="text"
                    className={`w-full ${
                      currencySymbol().length > 1 ? "pl-16" : "pl-10"
                    } pr-4 py-3 text-2xl font-semibold text-gray-900 bg-transparent border-b border-gray-200 focus:ring-0 focus:border-blue-500 dark:text-white dark:border-gray-700`}
                    placeholder="0.00"
                    value={
                      amount
                        ? new Intl.NumberFormat().format(Number(amount))
                        : ""
                    }
                    onChange={handleAmountChange}
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Description
              </label>
              <input
                type="text"
                className="w-full px-4 py-2.5 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                placeholder="e.g. Pay for services rendered"
                value={displayMessage}
                onChange={(e) => setDisplayMessage(e.target.value)}
                required
              />
            </div>

            <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
              <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-4">
                Payer Details
              </h4>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                    Payer Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2.5 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                    placeholder="Enter payer's full name or business name"
                    value={payerName}
                    onChange={(e) => setPayerName(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                    Payer Email
                  </label>
                  <input
                    type="email"
                    className="w-full px-4 py-2.5 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                    placeholder="Enter payer's email"
                    value={payerEmail}
                    onChange={(e) => setPayerEmail(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
              <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-4">
                Expire At (Optional)
              </h4>

              <div className="relative">
                <DatePicker
                  selected={expiresAtDate}
                  onChange={handleDateChange}
                  minDate={new Date()}
                  placeholderText="Select expiry date"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  dateFormat="yyyy-MM-dd"
                  isClearable
                />
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
              <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-4">
                Receivers Details
              </h4>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                    Receive to
                  </label>
                  {accountNameError && (
                    <div className="p-2 mb-2 text-red-700 bg-red-100 border border-red-400 rounded-md text-sm">
                      {accountNameError}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleReceiveToClick}
                    disabled={!selectedCurrency}
                    className={`w-full px-4 py-3 text-gray-700 bg-gray-50 border rounded-lg 
  dark:bg-gray-800 dark:border-gray-700 dark:text-white
  flex items-center justify-between
  ${
    !selectedCurrency
      ? "cursor-not-allowed opacity-50"
      : "cursor-pointer hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:hover:border-blue-500"
  }`}
                  >
                    <span className="truncate">
                      {paymentDetails?.paymentMethod === "WALLET"
                        ? `Wallet (${paymentDetails.accountName})`
                        : paymentDetails?.paymentMethod === "STABLE_CURRENCY" &&
                            paymentDetails?.walletAddress
                          ? `${paymentDetails.walletAddress.slice(0, 10)}...${paymentDetails.walletAddress.slice(-10)} (${paymentDetails.network})`
                          : receiveTo || "Select receiving method"}
                    </span>

                    <ChevronDownIcon className="h-5 w-5 text-gray-400 shrink-0" />
                  </button>
                </div>
              </div>
            </div>

            <div className="pt-4 pb-2 bg-white dark:bg-gray-900">
              <button
                onClick={handleCreatePaymentLink}
                disabled={
                  loading ||
                  !paymentDetails ||
                  !selectedCurrency ||
                  !amount ||
                  !payerName ||
                  !payerEmail ||
                  !displayMessage ||
                  !!accountNameError
                }
                className="w-full py-3 px-4 bg-[#EB2E67] text-white font-medium rounded-lg hover:bg-[#D8275A] focus:outline-none focus:ring-2 focus:ring-[#EB2E67] disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Spinner className="h-5 w-5 text-white mx-auto" />
                ) : (
                  "Create Payment Link"
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Method Type Selection Modal */}
      <PaymentLinkSelectMethod
        isOpen={showMethodTypeModal}
        onClose={() => setShowMethodTypeModal(false)}
        onSelect={handleMethodTypeSelect}
        title="Receive To"
        currency={selectedCurrency?.code || ""}
      />

      {/* Wallet Selection Modal */}
      <WalletSelectionModal
        isOpen={showWalletModal}
        onClose={() => setShowWalletModal(false)}
        onSelect={handleWalletSelect}
        currency={selectedCurrency?.code || ""}
      />

      {/* Payment Details Modal */}
      {selectedCurrency && (
        <PaymentDetailsModal
          isOpen={showPaymentDetailsModal}
          onClose={() => setShowPaymentDetailsModal(false)}
          onSave={handleSavePaymentDetails}
          currency={selectedCurrency.code}
          hasBankList={selectedCurrency?.hasBankList ?? false}
          paymentMethod={paymentMethod}
          toCurrency={{
            code: selectedCurrency.code,
            iso2: selectedCurrency.iso2,
            name: selectedCurrency.name,
            paymentMethod: selectedCurrency.paymentMethod,
          }}
          compareSelected={paymentDetails}
        />
      )}
    </Modal>
  );
};
